import { logger } from 'packages';

import { getSearchResults } from './api';
import actionTypes from './action-types';
import itemActions from '../items/actions';
import pageActions from '../page/actions';
import adsActions from '../ads/actions';
import { API_ERRORS } from 'tradera-utils/api';
import { reload } from 'tradera-utils/location';
import initialState from './initial-state';
import * as discoverTracking from 'tradera-apps/tradera/components/discover/helpers/search-gtm';
import {
    isSwedenCountryCodeOrUndefined,
    isSwedenCountryNameOrUndefined
} from 'tradera-utils/countrycode';
import { selectShippingRegionCountryCodeIso2 } from 'tradera-state/shipping-region/selectors';
import {
    selectIsLoggedIn,
    selectMemberCountryCodeIso2
} from 'tradera-state/member/selectors';

const requestSearchResults = () => {
    return {
        type: actionTypes.REQUEST_SEARCH_RESULTS
    };
};

const updatePageMetaForSearchResults = () => (dispatch, getState) => {
    const discover = getState().discover;
    const { reduxContainer } = discover;
    const { ads, pageMeta } = reduxContainer;
    dispatch(
        pageActions.setPageMeta({
            ...pageMeta
        })
    );
    if (ads) dispatch(adsActions.setAds(ads));
};

/**
 *
 * @param {object} payload the normalized version of the response
 * @param {object} payload.result
 * @param {object} payload.result.categoryPath
 * @param {object[]} [payload.result.fallbackItems]
 * @param {object[]} [payload.result.items]
 * @param {object} [payload.result.itemsOnDisplay]
 * @param {object[]} [payload.result.itemsOnDisplay.items]
 * @param {object} [payload.result.reduxContainer]
 * @param {object} [payload.result.reduxContainer.ads]
 * @param {object} [payload.result.reduxContainer.pageMeta]
 */
const receiveSearchResults = payload => dispatch => {
    const {
        result: { fallbackItems, items, itemsOnDisplay }
    } = payload;

    const itemsToAdd = [];
    if (fallbackItems?.length > 0) {
        itemsToAdd.push(...fallbackItems);
    }
    if (items?.length > 0) {
        itemsToAdd.push(...items);
    }
    if (itemsOnDisplay?.items?.length > 0) {
        itemsToAdd.push(...itemsOnDisplay.items);
    }
    if (itemsToAdd.length > 0) {
        dispatch(itemActions.add(itemsToAdd));
    }

    dispatch({
        type: actionTypes.RECEIVE_SEARCH_RESULTS,
        payload
    });
};

const fetchSearchResults = (endpoint, queryParams, pageType, onLoaded) => {
    return async (dispatch, getState) => {
        dispatch(requestSearchResults());
        const {
            environment: { version }
        } = getState();
        return getSearchResults(endpoint, queryParams, '', version)
            .then(({ data: result }) => {
                const searchResults = { result, endpoint, queryParams };
                dispatch(receiveSearchResults(searchResults));
                return searchResults;
            })
            .catch(error => {
                switch (error.message) {
                    case API_ERRORS.CANCELLED:
                        break;
                    case API_ERRORS.VERSION_MISMATCH:
                        reload(true);
                        break;
                    default:
                        dispatch(handleSearchResultsError(error.message));
                        break;
                }
                switch (error.message) {
                    case API_ERRORS.CANCELLED:
                    case API_ERRORS.NETWORK:
                    case API_ERRORS.TIMEOUT:
                    case API_ERRORS.VERSION_MISMATCH:
                        break;
                    default:
                        logger(error);
                        break;
                }
            })
            .finally(() => {
                dispatch(afterReceiveSearchResults(pageType, onLoaded));
            });
    };
};

const afterReceiveSearchResults = (pageType, onLoaded) => (
    dispatch,
    getState
) => {
    dispatch(updatePageMetaForSearchResults(pageType));
    dispatch(() => {
        trackNewSearch(getState);
        onLoaded();
    });
};

const handleSearchResultsError = payload => ({
    type: actionTypes.HANDLE_SEARCH_RESULTS_ERROR,
    payload
});

const setSingleFilterValue = (filter, value) => {
    return {
        type: actionTypes.SET_SINGLE_FILTER,
        payload: {
            filter,
            value
        }
    };
};

const addItemOnDisplay = payload => ({
    type: actionTypes.ADD_ITEM_ON_DISPLAY,
    payload
});

const removeItemOnDisplay = payload => ({
    type: actionTypes.REMOVE_ITEM_ON_DISPLAY,
    payload
});

const setCategoryPath = categoryPath => {
    return {
        type: actionTypes.SET_CATEGORY_PATH,
        payload: categoryPath
    };
};

const setCurrentCategoryId = categoryId => {
    return {
        type: actionTypes.SET_CURRENT_CATEGORY_ID,
        payload: categoryId
    };
};

const reset = () => (dispatch, getState) => {
    const state = getState();
    return dispatch({
        type: actionTypes.RESET,
        payload: {
            initialState: initialState(state.initData)
        }
    });
};

const initialize = () => (dispatch, getState) => {
    const shippingCountry = selectShippingRegionCountryCodeIso2(getState());
    const isLoggedIn = selectIsLoggedIn(getState());
    const memberCountry = selectMemberCountryCodeIso2(getState());
    const displayAllowedBuyerRegionFilter =
        !isSwedenCountryCodeOrUndefined(shippingCountry) ||
        (isLoggedIn && !isSwedenCountryNameOrUndefined(memberCountry));
    dispatch({
        type: actionTypes.INITIALIZE,
        payload: initialState({
            displayAllowedBuyerRegionFilter
        })
    });
};

const trackNewSearch = getState => {
    const state = getState();
    const { categoryPath, itemsById, metaTitle } = state.discover;
    discoverTracking.trackNewSearch(categoryPath, itemsById, metaTitle);
};

export default {
    afterReceiveSearchResults,
    addItemOnDisplay,
    fetchSearchResults,
    initialize,
    receiveSearchResults,
    removeItemOnDisplay,
    requestSearchResults,
    reset,
    setCategoryPath,
    setCurrentCategoryId,
    setSingleFilterValue
};
